import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listSignups, patchSignup, createGroup, listGroups, downloadCsv, clearToken } from "@/admin/api";
import {
  LogOut,
  Compass,
  Download,
  Users,
  Search,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  Copy,
  MessageCircle,
} from "lucide-react";

const EVENTS = [
  { value: "sundowner_cafes", label: "Sundowner cafés" },
  { value: "anjuna_nightlife", label: "Anjuna nightlife" },
  { value: "chapora_hike", label: "Chapora hike" },
];

const STATUS_LABELS = {
  pending: { label: "Pending", className: "bg-[#FFF4D6] text-[#7a4a00] border-[#FFC857]/40" },
  contacted: { label: "Contacted", className: "bg-[#E2EEFF] text-[#1b3a6b] border-[#9bb8e2]/50" },
  matched: { label: "Matched", className: "bg-[#E5F2E1] text-[#2c5a1c] border-[#9bc78d]/50" },
};

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("signups");

  const handleLogout = () => {
    clearToken();
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FF5A36] text-white">
              <Compass className="h-4 w-4" />
            </span>
            <span className="font-display font-extrabold tracking-tight">fourdays · admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-export-csv"
              onClick={async () => {
                try {
                  await downloadCsv();
                  toast.success("CSV downloaded");
                } catch {
                  toast.error("Couldn't download");
                }
              }}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-[#E5E0D8] bg-white px-3 py-1.5 hover:bg-[#FFEDE6]"
            >
              <Download className="h-4 w-4" /> CSV
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full bg-[#1A1A1A] text-white px-3 py-1.5 hover:bg-black"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-6 -mb-px flex gap-1">
          {[
            { v: "signups", label: "Signups & matching", icon: Users },
            { v: "groups", label: "Groups", icon: Sparkles },
          ].map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              data-testid={`admin-tab-${v}`}
              onClick={() => setTab(v)}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2.5 border-b-2 transition ${
                tab === v
                  ? "border-[#FF5A36] text-[#FF5A36]"
                  : "border-transparent text-[#5C5C5C] hover:text-[#1A1A1A]"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {tab === "signups" ? <SignupsTab /> : <GroupsTab />}
      </main>
    </div>
  );
}

function SignupsTab() {
  const [signups, setSignups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event: "", gender: "", women_only: "", status: "", hostel: "" });
  const [selected, setSelected] = useState(new Set());
  const [groupNotes, setGroupNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const fetchSignups = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_photo: true, limit: 200 };
      if (filters.event) params.event = filters.event;
      if (filters.gender) params.gender = filters.gender;
      if (filters.women_only !== "") params.women_only = filters.women_only;
      if (filters.status) params.status = filters.status;
      if (filters.hostel.trim()) params.hostel = filters.hostel.trim();
      const data = await listSignups(params);
      setSignups(data.signups || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Couldn't load signups");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSignups();
  }, [fetchSignups, refreshTick]);

  const selectedList = useMemo(
    () => signups.filter((s) => selected.has(s.id)),
    [signups, selected]
  );

  const groupEvent = selectedList[0]?.event || filters.event || "";
  const eventMismatch = selectedList.length >= 2 && selectedList.some((s) => s.event !== groupEvent);
  const hostelDup =
    selectedList.length >= 2 &&
    new Set(selectedList.map((s) => s.hostel_normalised)).size !== selectedList.length;
  const womenOnlyViolation =
    selectedList.some((s) => s.women_only) && selectedList.some((s) => s.gender !== "female");

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const onCreateGroup = async () => {
    if (selectedList.length < 2) {
      toast.error("Pick at least 2 travellers");
      return;
    }
    if (eventMismatch) {
      toast.error("All members must be for the same event");
      return;
    }
    setCreating(true);
    try {
      const res = await createGroup({
        event: groupEvent,
        signup_ids: selectedList.map((s) => s.id),
        notes: groupNotes || undefined,
      });
      if (res.warnings?.length) {
        toast.warning("Group created with warnings", { description: res.warnings.join(" ") });
      } else {
        toast.success("Group created");
      }
      clearSelection();
      setGroupNotes("");
      setRefreshTick((t) => t + 1);
    } catch {
      toast.error("Couldn't create group");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await patchSignup(id, { status });
      setSignups((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Couldn't update");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div>
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">
              Signups <span className="text-[#5C5C5C] font-bold">· {total}</span>
            </h1>
            <p className="text-sm text-[#5C5C5C] mt-1">Tap a card to add to the group on the right.</p>
          </div>
          <button
            onClick={() => setRefreshTick((t) => t + 1)}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-[#E5E0D8] bg-white px-3 py-1.5 hover:bg-[#FFEDE6]"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-white border border-[#E5E0D8] p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSelect
            testid="filter-event"
            label="Event"
            value={filters.event}
            onChange={(v) => setFilters((f) => ({ ...f, event: v }))}
            options={[{ value: "", label: "All events" }, ...EVENTS]}
          />
          <FilterSelect
            testid="filter-gender"
            label="Gender"
            value={filters.gender}
            onChange={(v) => setFilters((f) => ({ ...f, gender: v }))}
            options={[
              { value: "", label: "Any" },
              { value: "female", label: "Female" },
              { value: "male", label: "Male" },
              { value: "other", label: "Other" },
            ]}
          />
          <FilterSelect
            testid="filter-women-only"
            label="Women-only"
            value={filters.women_only}
            onChange={(v) => setFilters((f) => ({ ...f, women_only: v }))}
            options={[
              { value: "", label: "Any" },
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
          />
          <FilterSelect
            testid="filter-status"
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[
              { value: "", label: "Any" },
              { value: "pending", label: "Pending" },
              { value: "contacted", label: "Contacted" },
              { value: "matched", label: "Matched" },
            ]}
          />
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-[#5C5C5C]">Hostel</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
              <Input
                data-testid="filter-hostel"
                value={filters.hostel}
                onChange={(e) => setFilters((f) => ({ ...f, hostel: e.target.value }))}
                placeholder="hostel name"
                className="h-9 pl-7 mt-1 text-sm bg-[#FDFBF7] border-[#E5E0D8]"
              />
            </div>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div data-testid="signups-loading" className="text-sm text-[#5C5C5C] py-10 text-center">Loading…</div>
        ) : signups.length === 0 ? (
          <div className="text-sm text-[#5C5C5C] py-12 text-center border border-dashed border-[#E5E0D8] rounded-2xl bg-white">
            No signups match these filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="signups-grid">
            {signups.map((s) => (
              <SignupCard
                key={s.id}
                signup={s}
                selected={selected.has(s.id)}
                onToggle={() => toggleSelect(s.id)}
                onStatus={(st) => updateStatus(s.id, st)}
                onPhotoClick={() => setPreviewPhoto(s)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Photo preview dialog */}
      <Dialog open={!!previewPhoto} onOpenChange={(o) => !o && setPreviewPhoto(null)}>
        <DialogContent
          data-testid="photo-preview-dialog"
          className="p-0 bg-[#1A1A1A] border-0 sm:max-w-[640px] overflow-hidden"
        >
          {previewPhoto && (
            <div>
              <img
                src={previewPhoto.photo_data_url}
                alt={previewPhoto.name}
                className="w-full h-auto max-h-[80vh] object-contain bg-[#1A1A1A]"
              />
              <div className="px-5 py-4 bg-[#1A1A1A] text-white">
                <div className="font-display font-bold text-lg">
                  {previewPhoto.name} · {previewPhoto.age}
                </div>
                <div className="text-sm text-white/70 mt-1">
                  {previewPhoto.event_label} · @ {previewPhoto.hostel}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Group builder */}
      <aside className="lg:sticky lg:top-24 lg:h-fit" data-testid="group-builder">
        <div className="rounded-2xl bg-white border border-[#E5E0D8] p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-extrabold text-lg tracking-tight">Group builder</h2>
            <span className="text-xs font-semibold rounded-full bg-[#FFEDE6] text-[#FF5A36] px-2 py-1">
              {selectedList.length} selected
            </span>
          </div>

          {selectedList.length === 0 ? (
            <p className="mt-3 text-sm text-[#5C5C5C]">
              Tap signups on the left to add them. Aim for 3–5 travellers.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {selectedList.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2.5 rounded-xl border border-[#E5E0D8] bg-[#FDFBF7] px-2.5 py-2"
                >
                  {s.photo_data_url ? (
                    <img src={s.photo_data_url} alt="" className="h-8 w-8 rounded-lg object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-lg bg-[#E5E0D8]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.name}</div>
                    <div className="text-[11px] text-[#5C5C5C] truncate">{s.hostel}</div>
                  </div>
                  <button
                    onClick={() => toggleSelect(s.id)}
                    className="text-[11px] text-[#FF5A36] font-semibold"
                    data-testid={`remove-${s.id}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selectedList.length >= 2 && (
            <div className="mt-3 space-y-2">
              {eventMismatch && (
                <Warning text="Members are signed up for different events." />
              )}
              {hostelDup && <Warning text="Two or more members are from the same hostel." />}
              {womenOnlyViolation && (
                <Warning text="A member requested women-only but group includes non-female." />
              )}
              {!eventMismatch && !hostelDup && !womenOnlyViolation && (
                <div className="flex items-center gap-2 rounded-xl bg-[#E5F2E1] text-[#2c5a1c] border border-[#9bc78d]/50 px-3 py-2 text-xs font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Looks like a clean match.
                </div>
              )}
            </div>
          )}

          <div className="mt-4 space-y-1.5">
            <Label className="text-[10px] font-semibold uppercase tracking-widest text-[#5C5C5C]">
              Notes (optional)
            </Label>
            <Input
              data-testid="group-notes"
              value={groupNotes}
              onChange={(e) => setGroupNotes(e.target.value)}
              placeholder="e.g. Tue 6:30 PM Vagator cliffs"
              className="bg-[#FDFBF7] border-[#E5E0D8]"
            />
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              data-testid="create-group-button"
              onClick={onCreateGroup}
              disabled={creating || selectedList.length < 2 || eventMismatch}
              className="flex-1 h-11 rounded-xl bg-[#FF5A36] hover:bg-[#E0411F] text-white font-semibold"
            >
              {creating ? "Creating…" : "Create group"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={clearSelection}
              disabled={selectedList.length === 0}
              className="h-11 rounded-xl text-[#5C5C5C]"
              data-testid="clear-selection"
            >
              Clear
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function FilterSelect({ testid, label, value, onChange, options }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold uppercase tracking-widest text-[#5C5C5C]">{label}</Label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger
          data-testid={testid}
          className="h-9 mt-1 text-sm bg-[#FDFBF7] border-[#E5E0D8] focus:ring-[#FF5A36] focus:ring-offset-0"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value || "__all__"} value={o.value || "__all__"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Warning({ text }) {
  return (
    <div className="flex items-start gap-2 rounded-xl bg-[#FFF4D6] text-[#7a4a00] border border-[#FFC857]/50 px-3 py-2 text-xs font-semibold">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function SignupCard({ signup, selected, onToggle, onStatus, onPhotoClick }) {
  const status = signup.status || "pending";
  const meta = STATUS_LABELS[status] || STATUS_LABELS.pending;
  const fullNumber =
    signup.full_number || `${signup.country_code || ""}${signup.phone || ""}`;
  const waNumber = (fullNumber || "").replace(/[^\d]/g, "");
  const displayNumber = `${signup.country_code || ""} ${signup.phone || ""}`.trim();

  const copyNumber = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(fullNumber);
      toast.success("Number copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div
      data-testid={`signup-card-${signup.id}`}
      className={`group rounded-2xl bg-white border transition overflow-hidden ${
        selected ? "border-[#FF5A36] ring-2 ring-[#FF5A36]/30" : "border-[#E5E0D8] hover:border-[#FF5A36]/40"
      }`}
    >
      <div className="flex gap-3 p-3">
        {signup.photo_data_url ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPhotoClick && onPhotoClick();
            }}
            data-testid={`photo-thumb-${signup.id}`}
            className="shrink-0 group/photo relative"
            aria-label="View photo"
          >
            <img
              src={signup.photo_data_url}
              alt={signup.name}
              className="h-20 w-20 rounded-xl object-cover transition group-hover/photo:opacity-90"
            />
            <span className="absolute inset-0 rounded-xl bg-black/0 group-hover/photo:bg-black/15 transition" />
          </button>
        ) : (
          <div className="h-20 w-20 rounded-xl bg-[#E5E0D8] shrink-0" />
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
          data-testid={`select-${signup.id}`}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display font-bold text-[15px] tracking-tight truncate">{signup.name}</span>
            <span className="text-xs text-[#5C5C5C]">· {signup.age}</span>
            {signup.women_only && (
              <Badge className="bg-[#FFEDE6] text-[#FF5A36] border-[#FF5A36]/30 text-[10px] uppercase tracking-widest px-1.5 py-0">
                Women-only
              </Badge>
            )}
          </div>
          <div className="text-[12px] text-[#5C5C5C] mt-0.5">
            {signup.gender.replace("_", " ")} · {signup.country}
          </div>
          <div className="text-[12px] text-[#1A1A1A] mt-1 truncate">{signup.event_label}</div>
          {(signup.event_date || signup.event_time) && (
            <div className="text-[11px] text-[#5C5C5C] truncate">
              {signup.event_date &&
                new Date(signup.event_date + "T00:00:00").toLocaleDateString("en-GB", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              {signup.event_date && signup.event_time ? " · " : ""}
              {signup.event_time}
            </div>
          )}
          <div className="text-[11px] text-[#5C5C5C] truncate">@ {signup.hostel}</div>
          {signup.plus_one && (
            <div className="text-[10px] text-[#1A1A1A] mt-1">
              <span className="font-semibold text-[#FF5A36]">+1</span>{" "}
              {signup.plus_one_gender ? `(${signup.plus_one_gender})` : ""}
            </div>
          )}
          {signup.referral_source && (
            <div className="text-[10px] text-[#5C5C5C] mt-1 truncate">
              via <span className="font-semibold text-[#FF5A36]">{signup.referral_source}</span>
            </div>
          )}
        </button>
      </div>

      {/* WhatsApp row */}
      <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
        <span
          data-testid={`phone-${signup.id}`}
          className="text-[12px] font-mono font-semibold text-[#1A1A1A] bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg px-2 py-1"
        >
          {displayNumber || "—"}
        </span>
        <button
          type="button"
          data-testid={`copy-phone-${signup.id}`}
          onClick={copyNumber}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5C5C5C] hover:text-[#1A1A1A] border border-[#E5E0D8] hover:bg-[#FDFBF7] rounded-lg px-2 py-1"
          title="Copy WhatsApp number"
        >
          <Copy className="h-3 w-3" />
          Copy
        </button>
        {waNumber && (
          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`wa-${signup.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#25D366] hover:bg-[#1ebe5d] rounded-lg px-2 py-1"
            title="Open WhatsApp chat"
          >
            <MessageCircle className="h-3 w-3" />
            WhatsApp
          </a>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${meta.className}`}>
          {meta.label}
        </span>
        <div className="flex gap-1">
          {status !== "contacted" && (
            <button
              data-testid={`mark-contacted-${signup.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onStatus("contacted");
              }}
              className="text-[11px] font-semibold px-2 py-1 rounded-full border border-[#E5E0D8] hover:bg-[#FDFBF7]"
            >
              Mark contacted
            </button>
          )}
          {status !== "pending" && (
            <button
              data-testid={`mark-pending-${signup.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onStatus("pending");
              }}
              className="text-[11px] font-semibold px-2 py-1 rounded-full border border-[#E5E0D8] hover:bg-[#FDFBF7]"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await listGroups();
        setGroups(d.groups || []);
      } catch {
        toast.error("Couldn't load groups");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">
        Groups <span className="text-[#5C5C5C] font-bold">· {groups.length}</span>
      </h1>
      <p className="text-sm text-[#5C5C5C] mt-1">Every group you've matched, newest first.</p>

      {loading ? (
        <div className="text-sm text-[#5C5C5C] py-10 text-center">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="mt-6 text-sm text-[#5C5C5C] py-12 text-center border border-dashed border-[#E5E0D8] rounded-2xl bg-white">
          No groups yet. Create your first one from the Signups tab.
        </div>
      ) : (
        <div className="mt-5 grid md:grid-cols-2 gap-4" data-testid="groups-list">
          {groups.map((g) => (
            <article
              key={g.id}
              data-testid={`group-${g.id}`}
              className="rounded-2xl bg-white border border-[#E5E0D8] p-5"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display font-bold text-lg tracking-tight">{g.event_label}</h3>
                <span className="text-[11px] text-[#5C5C5C]">
                  {new Date(g.created_at).toLocaleString()}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {g.members_snapshot?.map((m) => (
                  <span
                    key={m.id}
                    className="text-xs font-semibold rounded-full bg-[#FFEDE6] text-[#FF5A36] px-2.5 py-1 border border-[#FF5A36]/20"
                  >
                    {m.name}
                  </span>
                ))}
              </div>
              {g.warnings?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {g.warnings.map((w, i) => (
                    <Warning key={i} text={w} />
                  ))}
                </div>
              )}
              {g.notes && <p className="mt-3 text-sm text-[#5C5C5C]">{g.notes}</p>}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
